import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { detectProvider, requestAccounts, WALLETS } from '../web3/walletConnect';
import { ensureCorrectNetwork, isCorrectChainId } from '../web3/networkSwitcher';
import { makeEthersProvider } from '../web3/providerHelper';
import { ACTIVE } from '../config/networks';

const WalletContext = createContext(null);

const STORAGE_KEY = 'adnft_wallet_kind';

export function WalletProvider({ children }) {
  const [account, setAccount] = useState(null);
  const [chainIdHex, setChainIdHex] = useState(null);
  const [walletKind, setWalletKind] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState(null);

  // Keep the raw injected provider + ethers wrapper without triggering re-renders
  const injectedRef = useRef(null);
  const ethersRef = useRef(null);

  function attachListeners(injected) {
    if (!injected || !injected.on) return;

    // ethers 5.7 requires we DON'T reset the provider on every network change,
    // but we do need to update state so the UI reflects the switch.
    const onAccounts = (accs) => {
      if (!accs || accs.length === 0) {
        disconnect();
      } else {
        setAccount(accs[0]);
      }
    };
    const onChain = (cid) => {
      setChainIdHex(cid);
      // Do NOT reload the page. Just re-wrap the provider so ethers picks up the change.
      try { ethersRef.current = makeEthersProvider(injected); } catch (_e) { /* ignore */ }
    };
    const onDisconnect = () => disconnect();

    injected.on('accountsChanged', onAccounts);
    injected.on('chainChanged', onChain);
    injected.on('disconnect', onDisconnect);

    // stash removers on injected object so we can detach later if needed
    injected._adnftDetach = () => {
      try { injected.removeListener && injected.removeListener('accountsChanged', onAccounts); } catch (_e) {}
      try { injected.removeListener && injected.removeListener('chainChanged', onChain); } catch (_e) {}
      try { injected.removeListener && injected.removeListener('disconnect', onDisconnect); } catch (_e) {}
    };
  }

  async function connect(kind) {
    setError(null);
    setConnecting(true);
    try {
      const injected = detectProvider(kind);
      if (!injected) throw new Error('No compatible wallet found. Please install or open inside a wallet browser.');
      const addr = await requestAccounts(injected);
      if (!addr) throw new Error('No account returned by wallet.');

      injectedRef.current = injected;
      ethersRef.current = makeEthersProvider(injected);
      attachListeners(injected);

      // Read chain, then trigger switch WITHOUT reload
      let cid = await injected.request({ method: 'eth_chainId' });
      setChainIdHex(cid);

      if (!isCorrectChainId(cid)) {
        setSwitching(true);
        try {
          await ensureCorrectNetwork(injected);
          cid = await injected.request({ method: 'eth_chainId' });
          setChainIdHex(cid);
          // Re-wrap ethers provider after chain change
          ethersRef.current = makeEthersProvider(injected);
        } finally {
          setSwitching(false);
        }
      }

      setAccount(addr);
      setWalletKind(kind);
      try { localStorage.setItem(STORAGE_KEY, kind); } catch (_e) {}
      return true;
    } catch (e) {
      setError(e && (e.message || String(e)));
      throw e;
    } finally {
      setConnecting(false);
    }
  }

  function disconnect() {
    try { if (injectedRef.current && injectedRef.current._adnftDetach) injectedRef.current._adnftDetach(); } catch (_e) {}
    injectedRef.current = null;
    ethersRef.current = null;
    setAccount(null);
    setChainIdHex(null);
    setWalletKind(null);
    try { localStorage.removeItem(STORAGE_KEY); } catch (_e) {}
  }

  async function switchNetworkNow() {
    if (!injectedRef.current) throw new Error('No wallet connected.');
    setSwitching(true);
    try {
      await ensureCorrectNetwork(injectedRef.current);
      const cid = await injectedRef.current.request({ method: 'eth_chainId' });
      setChainIdHex(cid);
      ethersRef.current = makeEthersProvider(injectedRef.current);
    } finally {
      setSwitching(false);
    }
  }

  // Silent reconnect on load if a wallet was previously selected AND still authorized
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (!saved) return;
        const injected = detectProvider(saved);
        if (!injected) return;
        const accs = await injected.request({ method: 'eth_accounts' });
        if (cancelled) return;
        if (accs && accs.length) {
          injectedRef.current = injected;
          ethersRef.current = makeEthersProvider(injected);
          attachListeners(injected);
          const cid = await injected.request({ method: 'eth_chainId' });
          setChainIdHex(cid);
          setAccount(accs[0]);
          setWalletKind(saved);
        }
      } catch (_e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const value = {
    account,
    chainIdHex,
    walletKind,
    connecting,
    switching,
    error,
    isCorrectNetwork: isCorrectChainId(chainIdHex),
    targetNetwork: ACTIVE,
    getInjected: () => injectedRef.current,
    getEthersProvider: () => ethersRef.current,
    getSigner: () => (ethersRef.current ? ethersRef.current.getSigner() : null),
    connect,
    disconnect,
    switchNetworkNow,
    WALLETS
  };

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export function useWallet() {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error('useWallet outside WalletProvider');
  return ctx;
}
